import React from "react";
import Header from "@/components/Header";
import Head from "next/head";
import { generateNextSeo } from "next-seo/pages";
import Footer from "@/components/Footer";
import Container from "@/components/blocks/Container";
import Link from "next/link";
import {
  ShieldExclamationIcon,
  EnvelopeIcon,
  ClockIcon,
  GlobeAltIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CommandLineIcon,
} from "@heroicons/react/24/outline";
import { docsColors, docsComponents, alertVariants } from "@/lib/docs-theme";

export default function TroubleshootingPage() {
  return (
    <React.Fragment>
      <Head>
        {generateNextSeo({
          title: "Troubleshooting - Uptime Monitor Documentation",
          description:
            "Common issues and solutions for Uptime Monitor. Firewall configuration, false positives, notification delivery, and more.",
          canonical: "https://uptime-monitor.io/docs/troubleshooting",
        })}
      </Head>
      <div className={docsComponents.pageContainer}>
        <Header />
        <main className="flex-1">
          <Container>
            <div className="mx-auto max-w-4xl px-4 py-12">
              {/* Breadcrumb */}
              <nav className="mb-8">
                <ol className="flex items-center space-x-2 text-sm">
                  <li>
                    <Link href="/docs" className={docsColors.link.muted}>
                      Documentation
                    </Link>
                  </li>
                  <li className={docsColors.text.muted}>/</li>
                  <li className={docsColors.text.heading}>Troubleshooting</li>
                </ol>
              </nav>

              {/* Page Title */}
              <h1 className={`text-4xl font-bold ${docsColors.text.heading} mb-4`}>
                Troubleshooting
              </h1>
              <p className={docsComponents.pageDescription}>
                Solutions to common issues and frequently asked questions about Uptime Monitor.
              </p>

              {/* Firewall Configuration */}
              <section className="mb-12">
                <h2
                  className={`text-2xl font-semibold ${docsColors.text.heading} mb-4 flex items-center`}
                >
                  <ShieldExclamationIcon className="h-6 w-6 mr-2" />
                  Firewall Configuration
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                    Whitelisting Uptime Monitor IP Addresses
                  </h3>
                  <p className={`${docsColors.text.body} mb-4`}>
                    If your server is behind a firewall, you&apos;ll need to whitelist our
                    monitoring IP addresses to allow checks to reach your services.
                  </p>

                  <div className={`${docsComponents.card} p-4 mb-4`}>
                    <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                      Get Current IP List:
                    </h4>
                    <div className="flex items-center space-x-2">
                      <CommandLineIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <code className={`text-sm ${docsColors.text.body} px-2 py-1 rounded`}>
                        curl https://uptime-monitor.io/web_api/checker_ip_addresses
                      </code>
                    </div>
                    <p className={`text-sm ${docsColors.text.muted} mt-2`}>
                      This endpoint returns all checker IPs grouped by region in plain text format.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                        Common Firewall Rules:
                      </h4>
                      <div className="space-y-2">
                        <div className={`${docsComponents.card} p-3`}>
                          <p className={`text-xs ${docsColors.text.muted} mb-1`}>
                            iptables (Linux):
                          </p>
                          <code className={`text-sm ${docsColors.text.body} font-mono`}>
                            iptables -A INPUT -s CHECKER_IP -p tcp --dport 443 -j ACCEPT
                          </code>
                        </div>
                        <div className={`${docsComponents.card} p-3`}>
                          <p className={`text-xs ${docsColors.text.muted} mb-1`}>ufw (Ubuntu):</p>
                          <code className={`text-sm ${docsColors.text.body} font-mono`}>
                            ufw allow from CHECKER_IP to any port 443
                          </code>
                        </div>
                        <div className={`${docsComponents.card} p-3`}>
                          <p className={`text-xs ${docsColors.text.muted} mb-1`}>
                            AWS Security Group:
                          </p>
                          <code className={`text-sm ${docsColors.text.body}`}>
                            Add inbound rule: HTTPS (443) from CHECKER_IP/32
                          </code>
                        </div>
                      </div>
                    </div>

                    <div className={`mt-4 p-4 ${alertVariants.warning} rounded border`}>
                      <div className="flex items-start">
                        <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-2 flex-shrink-0" />
                        <div>
                          <p className={`text-sm ${docsColors.text.body}`}>
                            <strong className={docsColors.text.heading}>Important:</strong> Our IP
                            addresses may change as we add new regions. Check the endpoint
                            periodically for updates.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* False Positives */}
              <section className="mb-12">
                <h2
                  className={`text-2xl font-semibold ${docsColors.text.heading} mb-4 flex items-center`}
                >
                  <ExclamationTriangleIcon className="h-6 w-6 mr-2" />
                  Reducing False Positives
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <p className={`${docsColors.text.body} mb-4`}>
                    False positives can cause alert fatigue. Here&apos;s how to minimize them:
                  </p>

                  <div className="space-y-4">
                    <div>
                      <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                        1. Configure Multiple Regions
                      </h3>
                      <p className={`${docsColors.text.body} mb-2`}>
                        Enable checks from multiple regions for better reliability:
                      </p>
                      <ul className={`space-y-1 text-sm ${docsColors.text.muted} ml-4`}>
                        <li>• Reduces impact of regional network problems</li>
                        <li>• Faster failure detection with more check points</li>
                        <li>• Provides geographic redundancy</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                        2. Adjust Timeout Settings
                      </h3>
                      <p className={`${docsColors.text.body} mb-2`}>
                        Set realistic timeout values based on your service&apos;s normal response
                        times:
                      </p>
                      <div className={`${docsComponents.card} p-4`}>
                        <p className={`text-sm ${docsColors.text.body}`}>
                          <strong className={docsColors.text.heading}>Recommended:</strong> Set
                          timeout to 3-5x your average response time
                        </p>
                        <p className={`text-xs ${docsColors.text.muted} mt-1`}>
                          Example: If average is 500ms, use 1500-2500ms timeout
                        </p>
                      </div>
                    </div>

                    <div>
                      <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                        3. Understand Fail Threshold
                      </h3>
                      <p className={`${docsColors.text.body} mb-2`}>
                        Monitors require multiple consecutive failures before alerting:
                      </p>
                      <ul className={`space-y-1 text-sm ${docsColors.text.muted} ml-4`}>
                        <li>• Default: 2 consecutive failures required</li>
                        <li>• Checks from any region count toward threshold</li>
                        <li>• One successful check marks monitor as UP</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Email Delivery Issues */}
              <section className="mb-12">
                <h2
                  className={`text-2xl font-semibold ${docsColors.text.heading} mb-4 flex items-center`}
                >
                  <EnvelopeIcon className="h-6 w-6 mr-2" />
                  Email Notification Issues
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <div className="space-y-4">
                    <div>
                      <h3 className={`font-semibold ${docsColors.text.heading} mb-2`}>
                        Not Receiving Emails?
                      </h3>
                      <ol className={`space-y-2 text-sm ${docsColors.text.body}`}>
                        <li className="flex items-start">
                          <span className={`${docsColors.icon.primary} font-semibold mr-2`}>
                            1.
                          </span>
                          <div>
                            <strong>Check spam/junk folder</strong>
                            <p className={docsColors.text.muted}>
                              Emails from alert@m.uptime-monitor.io may be filtered
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold mr-2">
                            2.
                          </span>
                          <div>
                            <strong>Whitelist our domain</strong>
                            <p className={docsColors.text.muted}>
                              Add uptime-monitor.io to your email whitelist
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold mr-2">
                            3.
                          </span>
                          <div>
                            <strong>Verify contact email</strong>
                            <p className={docsColors.text.muted}>
                              Ensure the email address in your contact is correct
                            </p>
                          </div>
                        </li>
                        <li className="flex items-start">
                          <span className="text-blue-600 dark:text-blue-400 font-semibold mr-2">
                            4.
                          </span>
                          <div>
                            <strong>Test email delivery</strong>
                            <p className={docsColors.text.muted}>
                              Send a test notification from contact settings
                            </p>
                          </div>
                        </li>
                      </ol>
                    </div>

                    <div className={`p-4 ${alertVariants.info} rounded border`}>
                      <p className={`text-sm ${docsColors.text.body}`}>
                        <strong className={docsColors.text.heading}>Tip:</strong> Send a test
                        notification from the contact settings to verify email delivery is working.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* SSL Certificate Errors */}
              <section className="mb-12">
                <h2
                  className={`text-2xl font-semibold ${docsColors.text.heading} mb-4 flex items-center`}
                >
                  <LockClosedIcon className="h-6 w-6 mr-2" />
                  SSL Certificate Validation
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                    Common SSL Issues
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                        Certificate Chain Errors
                      </h4>
                      <p className={`${docsColors.text.body} mb-2`}>
                        Ensure your server sends the complete certificate chain:
                      </p>
                      <ul className={`space-y-1 text-sm ${docsColors.text.muted} ml-4`}>
                        <li>• Include intermediate certificates</li>
                        <li>• Order certificates correctly (server → intermediate → root)</li>
                        <li>• Use SSL checker tools to validate configuration</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                        Self-Signed Certificates
                      </h4>
                      <p className={`${docsColors.text.body} mb-2`}>
                        Uptime Monitor validates certificates against trusted CAs:
                      </p>
                      <ul className={`space-y-1 text-sm ${docsColors.text.muted} ml-4`}>
                        <li>• Self-signed certificates will fail validation</li>
                        <li>• Use Let&apos;s Encrypt for free trusted certificates</li>
                        <li>• Consider disabling SSL validation for internal services</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                        Expiration Warnings
                      </h4>
                      <p className={docsColors.text.body}>
                        Enable &quot;Warn about SSL expiration&quot; to receive alerts at 30, 14, 7,
                        and 1 day before certificate expiry.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Monitor Status Issues */}
              <section className="mb-12">
                <h2
                  className={`text-2xl font-semibold ${docsColors.text.heading} mb-4 flex items-center`}
                >
                  <GlobeAltIcon className="h-6 w-6 mr-2" />
                  Monitor Status Accuracy
                </h2>
                <div className={`${docsComponents.card} p-6`}>
                  <h3 className={`font-semibold ${docsColors.text.heading} mb-3`}>
                    Understanding Monitor Status
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                        Status Determination
                      </h4>
                      <p className={`${docsColors.text.body} mb-2`}>
                        Monitor status is based on recent check results:
                      </p>
                      <ul className={`space-y-1 text-sm ${docsColors.text.muted} ml-4`}>
                        <li>
                          • <span className="text-green-600 dark:text-green-400">UP:</span> At least
                          one recent check succeeded
                        </li>
                        <li>
                          • <span className="text-red-600 dark:text-red-400">DOWN:</span> Multiple
                          consecutive failures (based on fail threshold)
                        </li>
                      </ul>
                    </div>

                    <div>
                      <h4 className={`font-medium ${docsColors.text.heading} mb-2`}>
                        Response Time Calculations
                      </h4>
                      <p className={docsColors.text.body}>
                        Response times are averaged across all successful checks from different
                        regions to provide a global performance metric.
                      </p>
                    </div>

                    <div className={`p-4 ${alertVariants.info} rounded border`}>
                      <p className={`text-sm ${docsColors.text.body}`}>
                        <strong className={docsColors.text.heading}>Note:</strong> Status updates
                        reflect immediately after checks complete. The fail threshold determines how
                        many failures are needed before marking as DOWN.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Getting Help */}
              <section className="mb-12">
                <div className={`p-6 ${alertVariants.info} rounded border`}>
                  <h2 className={`text-xl font-semibold ${docsColors.text.heading} mb-3`}>
                    Still Need Help?
                  </h2>
                  <p className={`${docsColors.text.body} mb-4`}>
                    If you&apos;re experiencing issues not covered here, we are ready to assist.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="mailto:support@uptime-monitor.io"
                      className="inline-flex items-center justify-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      Contact Support
                    </a>
                  </div>
                </div>
              </section>

              {/* Footer Navigation */}
              <div className="flex justify-between items-center pt-8 border-t border-slate-700">
                <Link href="/docs/best-practices" className={docsColors.link.muted}>
                  ← Best Practices
                </Link>
                <Link href="/docs" className={docsColors.link.primary}>
                  Documentation Home →
                </Link>
              </div>
            </div>
          </Container>
        </main>
        <Footer />
      </div>
    </React.Fragment>
  );
}
